import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FaAppleAlt, FaPrint, FaRegClock } from 'react-icons/fa';
import CustomSpinner from '../../../Components/CommonComponents/CustomSpinner';
import { DIET_SECTIONS, DIET_FIELDS } from '../../../Files/dietForm';
import { fetchPatientTherapyForms } from '../../../Services/patientProfile';

const hasAnswer = (value) => value !== undefined && value !== null && String(value).trim() !== '';

/** Mongo ids start with a 4-byte timestamp, which is when the patient submitted. */
const submittedAt = (record) => {
  const id = String(record?._id || '');
  if (id.length < 8) return null;
  const seconds = parseInt(id.slice(0, 8), 16);
  return Number.isNaN(seconds) ? null : new Date(seconds * 1000);
};

const fmtDate = (date) =>
  date
    ? date.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'Date unknown';

const Answer = ({ field, value }) => (
  <div className="border-b border-slate-100 py-2.5 last:border-b-0 sm:flex sm:gap-4">
    <dt className="text-xs leading-relaxed text-slate-500 sm:w-1/2 sm:shrink-0">
      {field.label}
      {field.unit && <span className="ml-1 text-slate-400">({field.unit})</span>}
    </dt>
    <dd className="mt-0.5 whitespace-pre-wrap break-words text-sm font-medium text-slate-800 sm:mt-0 sm:w-1/2">{value}</dd>
  </div>
);

const SubmissionCard = ({ record, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const answers = record?.requestForm || {};

  const sections = useMemo(
    () =>
      DIET_SECTIONS.map((section) => ({
        ...section,
        answered: section.fields.filter((f) => hasAnswer(answers[f.name])),
      })).filter((section) => section.answered.length),
    [answers]
  );

  // Anything the patient sent that the current questionnaire no longer asks —
  // shown so an older submission never silently loses answers.
  const extras = useMemo(() => {
    const known = new Set(DIET_FIELDS.map((f) => f.name));
    return Object.entries(answers).filter(([key, value]) => !known.has(key) && key !== 'patientId' && hasAnswer(value));
  }, [answers]);

  const totalAnswered = sections.reduce((sum, s) => sum + s.answered.length, 0) + extras.length;
  const when = submittedAt(record);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-slate-50"
      >
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <FaRegClock className="h-3 w-3 shrink-0 text-slate-400" />
            {fmtDate(when)}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            {totalAnswered} {totalAnswered === 1 ? 'answer' : 'answers'}
            {record?.status && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 font-medium capitalize text-slate-600">{record.status}</span>}
          </p>
        </div>
        <span className="shrink-0 text-xs font-semibold text-primary-600">{open ? 'Hide' : 'View'}</span>
      </button>

      {open && (
        <div className="border-t border-slate-100 px-5 py-4">
          {sections.map((section) => (
            <div key={section.id} className="mb-6 last:mb-0">
              <h4 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">{section.title}</h4>
              <dl>
                {section.answered.map((field) => (
                  <Answer key={field.name} field={field} value={answers[field.name]} />
                ))}
              </dl>
            </div>
          ))}

          {extras.length > 0 && (
            <div>
              <h4 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">Other answers</h4>
              <dl>
                {extras.map(([key, value]) => (
                  <Answer key={key} field={{ label: key }} value={String(value)} />
                ))}
              </dl>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function DietTab({ patientId }) {
  const [records, setRecords] = useState(null);

  const load = useCallback(async () => {
    try {
      setRecords(await fetchPatientTherapyForms(patientId, { therapy: 'nutrition' }));
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not load diet forms');
      setRecords([]);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (records === null) return <CustomSpinner />;

  if (!records.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
        <FaAppleAlt className="mx-auto h-7 w-7 text-slate-300" />
        <p className="mt-3 text-sm font-semibold text-slate-700">No diet questionnaire yet</p>
        <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-slate-500">
          Send this patient the Diet link from their prescription. Once they fill it in, their answers appear here.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-bold text-slate-800">Diet questionnaire</h3>
          <p className="text-xs text-slate-500">
            {records.length} {records.length === 1 ? 'submission' : 'submissions'}, newest first
          </p>
        </div>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
        >
          <FaPrint className="h-3 w-3" />
          Print
        </button>
      </div>

      <div className="space-y-3">
        {records.map((record, index) => (
          <SubmissionCard key={record._id} record={record} defaultOpen={index === 0} />
        ))}
      </div>
    </div>
  );
}
